<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { useStore } from 'vuex';
  import { ref } from 'vue';

  // Setup
  defineOptions({
    name: 'DefaultSidebar'
  })

  // References
  const isAdmin = ref(false);
  const isMobileMenuOpen = ref(false);

  // Grab stored values
  const store = useStore();
  const user = store.state.user;
  const apps = store.state.apps;

  if (user.role === 'admin') {
    isAdmin.value = true;
  }

  // Methods
  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
  }

  defineExpose({
    toggleMobileMenu
  })
</script>

<template>
  <!-- Desktop Sidebar -->
  <aside class="hidden md:flex sidebar fixed flex-col justify-between h-[calc(100vh-100px)] w-[60px] mt-[10px] rounded-tr-[10px] rounded-br-[10px] z-[49] transition-[width] duration-300 ease-in-out overflow-hidden hover:w-[150px] bg-color-a dark:bg-color-a-darkmode">
    <!-- Sidebar content starting from the top -->
    <div>
      <!-- Display options for products -->
      <RouterLink to="/dashboard" class="flex items-center bg-transparent cursor-pointer overflow-hidden w-full pl-[10px] mt-[10px] border-b border-solid border-b-white dark:border-b-dark-text pb-[10px] hover:bg-color-a-darker">
        <img src="@/assets/images/home_white.png" alt="Home" class="w-[35px] h-[35px] rounded-full transition-[margin] duration-300 ease-in-out">
        <span class="opacity-0 ml-[10px] whitespace-nowrap transition-opacity duration-300 ease-in-out sidebar-hover:opacity-100 text-white dark:text-dark-text">Home</span>
      </RouterLink>
      <RouterLink to="/dashboard" class="flex items-center bg-transparent border-none cursor-pointer overflow-hidden w-full pl-[10px] mt-[20px] hover:bg-color-a-darker">
        <img src="@/assets/images/gauge_white.png" alt="Gauge" class="w-[35px] h-[35px] rounded-full transition-[margin] duration-300 ease-in-out">
        <span class="opacity-0 ml-[10px] whitespace-nowrap transition-opacity duration-300 ease-in-out sidebar-hover:opacity-100 text-white dark:text-dark-text">Dashboard</span>
      </RouterLink>
      <RouterLink to="/sim-cards" class="flex items-center bg-transparent border-none cursor-pointer overflow-hidden w-full pl-[10px] mt-[30px] hover:bg-color-a-darker">
        <font-awesome-icon :icon="['fas', 'sim-card']" class="w-[35px] h-[35px] rounded-full transition-[margin] duration-300 ease-in-out text-white white" />
        <span class="opacity-0 ml-[10px] whitespace-nowrap transition-opacity duration-300 ease-in-out sidebar-hover:opacity-100 text-white dark:text-dark-text">Sim Cards</span>
      </RouterLink>
      <!-- Display pinned external application -->
      <div v-if="user.apps.length > 0">
        <div class="border-b border-dashed border-b-white dark:border-b-dark-text mt-[30px]"></div>
        <RouterLink v-for="appId in user.apps" :key="appId" :to="`/applications/${apps.find(app => app.id === appId).path}`" class="flex items-center bg-transparent border-none cursor-pointer overflow-hidden w-full pl-[10px] mt-[30px] hover:bg-color-a-darker">
          <img :src="apps.find(app => app.id === appId).icon" alt="Bluetooth" class="w-[35px] h-[35px] rounded-lg transition-[margin] duration-300 ease-in-out text-white" />
          <span class="opacity-0 ml-[10px] whitespace-nowrap transition-opacity duration-300 ease-in-out sidebar-hover:opacity-100 text-white dark:text-dark-text">Bluetooth</span>
        </RouterLink>
      </div>
      <!-- Display external application menu -->
      <div v-if="apps.length > 0">
        <div class="border-b border-dashed border-b-white dark:border-b-dark-text mt-[30px]"></div>
        <RouterLink to="/applications" class="flex items-center bg-transparent border-none cursor-pointer overflow-hidden w-full pl-[10px] mt-[30px] hover:bg-color-a-darker">
          <font-awesome-icon :icon="['fas', 'grip-vertical']" class="w-[35px] h-[35px] rounded-full transition-[margin] duration-300 ease-in-out text-white white" />
          <span class="opacity-0 ml-[10px] whitespace-nowrap transition-opacity duration-300 ease-in-out sidebar-hover:opacity-100 text-white dark:text-dark-text">Apps</span>
        </RouterLink>
      </div>
    </div>
  
    <!-- Settings icon at the bottom -->
    <div class="mb-[10px]">
      <RouterLink v-if="isAdmin" to="/settings" class="flex items-center bg-transparent border-none cursor-pointer overflow-hidden w-full pl-[10px] mt-[10px] hover:bg-color-a-darker">
          <img src="@/assets/images/gear_white.png" alt="Gear" class="w-[35px] h-[35px] rounded-full transition-[margin] duration-300 ease-in-out">
          <span class="opacity-0 ml-[10px] whitespace-nowrap transition-opacity duration-300 ease-in-out sidebar-hover:opacity-100 text-white dark:text-dark-text">Settings</span>
      </RouterLink>
    </div>
  </aside>

  <!-- Mobile Navigation Menu Overlay -->
  <div 
    v-if="isMobileMenuOpen"
    class="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
    @click="toggleMobileMenu"
  ></div>
  
  <!-- Mobile Navigation Menu -->
  <nav 
    class="md:hidden fixed top-0 left-0 w-[80%] max-w-[300px] h-screen bg-color-a dark:bg-color-a-darkmode z-40 rounded-tr-2xl shadow-xl transform transition-transform duration-300 ease-in-out"
    :class="isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
  >
    <!-- Close button -->
    <button 
      @click="toggleMobileMenu"
      class="absolute top-4 right-4 p-2 text-white hover:bg-color-a-darker rounded-full"
    >
      <svg 
        class="w-6 h-6"
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          stroke-linecap="round" 
          stroke-linejoin="round" 
          stroke-width="2" 
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>

    <!-- Menu content -->
    <div class="flex flex-col h-full p-4 pt-16 overflow-y-auto">
      <div class="flex-1 space-y-4">
        <!-- Home -->
        <RouterLink 
          @click="toggleMobileMenu"
          to="/dashboard" 
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-color-a-darker"
        >
          <img src="@/assets/images/home_white.png" alt="Home" class="w-6 h-6">
          <span class="text-white dark:text-dark-text">Home</span>
        </RouterLink>

        <!-- Dashboard -->
        <RouterLink 
          @click="toggleMobileMenu"
          to="/dashboard" 
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-color-a-darker"
        >
          <img src="@/assets/images/gauge_white.png" alt="Gauge" class="w-6 h-6">
          <span class="text-white dark:text-dark-text">Dashboard</span>
        </RouterLink>

        <!-- Sim Cards -->
        <RouterLink 
          @click="toggleMobileMenu"
          to="/sim-cards" 
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-color-a-darker"
        >
          <font-awesome-icon :icon="['fas', 'sim-card']" class="w-6 h-6 text-white" />
          <span class="text-white dark:text-dark-text">Sim Cards</span>
        </RouterLink>

        <!-- Pinned Apps -->
        <template v-if="user.apps.length > 0">
          <div class="border-t border-white border-opacity-20 my-4"></div>
          <RouterLink 
            v-for="appId in user.apps" 
            :key="appId" 
            :to="`/applications/${apps.find(app => app.id === appId).path}`"
            @click="toggleMobileMenu"
            class="flex items-center space-x-3 p-3 rounded-lg hover:bg-color-a-darker"
          >
            <img 
              :src="apps.find(app => app.id === appId).icon" 
              :alt="apps.find(app => app.id === appId).name" 
              class="w-6 h-6"
            >
            <span class="text-white dark:text-dark-text">
              {{ apps.find(app => app.id === appId).name }}
            </span>
          </RouterLink>
        </template>

        <!-- Apps Menu -->
        <template v-if="apps.length > 0">
          <div class="border-t border-white border-opacity-20 my-4"></div>
          <RouterLink 
            @click="toggleMobileMenu"
            to="/applications" 
            class="flex items-center space-x-3 p-3 rounded-lg hover:bg-color-a-darker"
          >
            <font-awesome-icon :icon="['fas', 'grip-vertical']" class="w-6 h-6 text-white" />
            <span class="text-white dark:text-dark-text">Apps</span>
          </RouterLink>
        </template>
      </div>

      <!-- Settings at the bottom -->
      <div class="pt-4 border-t border-white border-opacity-20">
        <RouterLink 
          v-if="isAdmin"
          @click="toggleMobileMenu"
          to="/settings" 
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-color-a-darker"
        >
          <img src="@/assets/images/gear_white.png" alt="Gear" class="w-6 h-6">
          <span class="text-white dark:text-dark-text">Settings</span>
        </RouterLink>
      </div>
    </div>
  </nav>
</template>
  
<style>
/* We only need this one custom modifier for the hover effect */
.sidebar:hover .sidebar-hover\:opacity-100 {
  opacity: 1;
}

/* Add padding to the main content area on mobile to account for the bottom navigation */
@media (max-width: 768px) {
  main {
    padding-bottom: 4rem;
  }
}
</style>
